import { type CSSProperties, useEffect, useRef, useState } from "react";
import type { KeyState } from "../wordle/keystate";

interface NotebookCellProps {
  letter: string;
  state: KeyState | null;
  /** Draw the mark (highlighter / hatching) now; `delay` staggers it along the row. */
  reveal: boolean;
  delay: number;
  /** A letter typed into the active row writes itself in; restored rows just sit there. */
  write: boolean;
  /** Handwriting wobble, deterministic per cell. */
  tilt: number;
  lift: number;
}

/** One notebook square: a handwritten letter over its mark. Erasing smudges the old letter away. */
export function NotebookCell({ letter, state, reveal, delay, write, tilt, lift }: NotebookCellProps) {
  const prev = useRef(letter);
  const [ghost, setGhost] = useState<string | null>(null);

  useEffect(() => {
    const was = prev.current;
    prev.current = letter;
    if (!was || letter) return undefined;
    setGhost(was);
    const t = window.setTimeout(() => setGhost(null), 180);
    return () => window.clearTimeout(t);
  }, [letter]);

  const style = { "--tilt": `${tilt}deg`, "--lift": `${lift}px`, "--d": `${delay}ms` } as CSSProperties;
  return (
    <div className={`nb-cell ${reveal ? "nb-reveal" : ""}`} data-s={state ?? ""} style={style}>
      {letter ? (
        <span key={letter} className={`nb-glyph ${write ? "nb-write" : ""}`}>
          {letter}
        </span>
      ) : ghost ? (
        <span className="nb-glyph nb-erase" aria-hidden="true">
          {ghost}
        </span>
      ) : null}
    </div>
  );
}
